/** Preferences › Driver declarations — "confirm before site/bay access" wording, per tenant or per site. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { listDeclarationNotices, saveDeclarationNotice, DEFAULT_DECLARATIONS_NOTICE } from "@/lib/api";

const NoticeRow = ({ label, row, placeholder, onSave, testId }) => {
  const [text, setText] = useState(row?.notice || "");
  useEffect(() => setText(row?.notice || ""), [row]);
  const dirty = text.trim() !== (row?.notice || "");
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="text-xs font-medium text-slate-600">{label}</div>
      <Textarea rows={2} maxLength={600} value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)}
                data-testid={`${testId}-input`} />
      {dirty && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => onSave(text, row?.id)} data-testid={`${testId}-save`}>
            {text.trim() ? "Save" : "Reset to default"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default function DeclarationNoticeEditor({ clientId, sites }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const { data, error } = await listDeclarationNotices(clientId);
    if (error) toast.error(error.message);
    setRows(data || []);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const save = async (siteId, text, id) => {
    if (text.trim() && text.trim().length < 3) { toast.error("Notice needs at least 3 characters"); return; }
    const { error } = await saveDeclarationNotice(clientId, siteId, text, id);
    if (error) { toast.error(error.message); return; }
    toast.success(text.trim() ? "Notice saved" : "Notice reset to default");
    load();
  };
  const tenantRow = rows.find((r) => !r.site_id);
  const tenantText = tenantRow?.notice || DEFAULT_DECLARATIONS_NOTICE;

  return (
    <Card data-testid="decl-notice-card">
      <CardHeader>
        <CardTitle className="text-base">Site access notice</CardTitle>
        <p className="text-sm text-slate-500">Shown next to mandatory (red *) declarations on the booking form, the booking
          record and in booking, driver and reminder emails. Unticked mandatory items don&apos;t block the booking; they&apos;re
          flagged &ldquo;to confirm at arrival&rdquo;. Leave a site blank to use the tenant wording.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <NoticeRow label="All sites (tenant wording)" row={tenantRow} placeholder={DEFAULT_DECLARATIONS_NOTICE}
                   onSave={(t, id) => save(null, t, id)} testId="decl-notice-tenant" />
        {sites.map((s) => (
          <NoticeRow key={s.id} label={s.name} row={rows.find((r) => r.site_id === s.id)} placeholder={tenantText}
                     onSave={(t, id) => save(s.id, t, id)} testId={`decl-notice-site-${s.id}`} />
        ))}
      </CardContent>
    </Card>
  );
}
